import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { products } from '../utils/products';
import { useApp } from '../context/AppContext';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const CatalogPage = ({ category = 'all' }) => {
  const { t } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 24;

  const getCategoryTitle = (cat) => {
    switch(cat) {
      case 'ppe': return t.categories.ppe;
      case 'tct': return t.categories.tct;
      case 'enm': return t.categories.enm;
      case 'mydTools': return t.categories.mydTools;
      case 'ept': return t.categories.ept;
      case 'heightWork': return t.categories.heightWork;
      case 'isolatedTools': return t.categories.isolatedTools;
      case 'manualandmotorTools': return t.categories.manualandmotorTools;
      default: return 'Todos los Productos';
    }
  };

  const allProducts = useMemo(() => {
    return [...products.ppe, ...products.tct, ...products.enm, ...products.mydTools, ...products.ept, ...products.heightWork, ...products.isolatedTools, ...products.manualandmotorTools];
  }, []);

  const getProductsToShow = () => {
    if (category === 'all' || category === 'catalog') {
      return allProducts;
    }
    return products[category] || [];
  };

  const filteredProducts = useMemo(() => {
    const baseProducts = getProductsToShow();

    if (!searchTerm.trim()) {
      return baseProducts;
    }

    const searchLower = searchTerm.toLowerCase();
    return baseProducts.filter(product =>
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      product.reference.toLowerCase().includes(searchLower) ||
      product.category.toLowerCase().includes(searchLower) ||
      (product.features && product.features.some(feature =>
        feature.toLowerCase().includes(searchLower)
      ))
    );
  }, [searchTerm, category, allProducts]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * productsPerPage;
    return filteredProducts.slice(startIndex, startIndex + productsPerPage);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, category]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white/50 via-black/95 to-black py-12">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-blue-800 bg-clip-text text-transparent">
            {getCategoryTitle(category)}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Encuentra los mejores equipos y herramientas especializados para tu trabajo...
          </p>
        </motion.div>

        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-12 pr-12 py-4 shadow-lg shadow-gray-700 border-2 border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-lg placeholder-gray-500"
                placeholder={t.catalog?.search || "Buscar productos..."}
              />
              
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  <motion.button
                    onClick={clearSearch}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </motion.button>
                </div>
              )}
            </div>

            {searchTerm && (
              <motion.div
                className="mt-4 flex items-center gap-2 text-gray-600"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm">
                  {t.catalog?.showingResults || "Mostrando resultados para:"} 
                  <span className="font-semibold text-blue-500 ml-1">"{searchTerm}"</span>
                  <span className="ml-2 bg-gradient-to-r from-yellow-500 to-yellow-800 text-white px-2 py-1 rounded-full text-xs font-bold">
                    {filteredProducts.length} Productos
                  </span>
                </span>
              </motion.div>
            )}
          </div>
        </motion.div>

        {filteredProducts.length > 0 && totalPages > 1 && (
          <div className="flex justify-center pb-10 mt-8 gap-2 flex-wrap">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <motion.button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 flex items-center justify-center font-semibold transition-all ${currentPage === page
                    ? 'bg-gradient-to-r from-blue-500 to-blue-800 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-200'
                  }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {page}
              </motion.button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {paginatedProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} index={index} />
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <motion.div
            className="text-center py-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-600 mb-2">
              {searchTerm ? (t.catalog?.noResults || "No se encontraron productos") : "No hay productos disponibles"}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {searchTerm 
                ? `No encontramos productos que coincidan con "${searchTerm}". Intenta con otros términos de búsqueda.`
                : "Pronto agregaremos más productos a esta categoría."
              }
            </p>
            {searchTerm && (
              <motion.button
                onClick={clearSearch}
                className="mt-4 bg-gradient-to-r shadow-lg shadow-gray-700 from-blue-500 to-blue-800 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Ver todos los productos
              </motion.button>
            )}
          </motion.div> 
        )}
      </div>
    </div>
  );
};

export default CatalogPage;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.